import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";

export const RightsSettingsDialog = ({ open, onOpenChange, meta, onSaved }) => {
  const [sla, setSla] = useState(30);
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !meta) return;
    const s = meta.settings;
    setSla(s.sla_days);
    setRows(Object.fromEntries(Object.entries(s.verification).map(([k, v]) => [k, { methods: v.methods.join(", "), required_checks: v.required_checks }])));
  }, [open, meta]);

  const set = (k, field) => (e) => setRows((x) => ({ ...x, [k]: { ...x[k], [field]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const verification = Object.fromEntries(Object.entries(rows).map(([k, v]) => [k, {
        methods: v.methods.split(",").map((m) => m.trim()).filter(Boolean), required_checks: Number(v.required_checks) || 1 }]));
      await api.put("/rights/settings", { sla_days: Number(sla), verification });
      toast.success("Rights request settings saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  if (!meta) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rights-settings-dialog">
        <DialogHeader>
          <DialogTitle>Identity verification & SLA</DialogTitle>
          <DialogDescription>These settings apply to this organization only. Officers record each check; no OTPs are sent.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Label className="text-xs w-44">Default target (days after receipt)</Label>
            <Input type="number" min={1} max={365} value={sla} onChange={(e) => setSla(e.target.value)} className="w-24 h-9 font-mono"
                   data-testid="settings-sla-days" />
          </div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            <div className="grid grid-cols-[110px_1fr_90px] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              <span>Subject</span><span>Verification methods (comma separated)</span><span>Required</span>
            </div>
            {Object.entries(rows).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[110px_1fr_90px] gap-3 px-3 py-2 items-center">
                <span className="text-sm font-medium text-[#003366]">{meta.subject_types[k]}</span>
                <Input value={v.methods} onChange={set(k, "methods")} className="h-8 text-xs" data-testid={`settings-methods-${k.toLowerCase()}`} />
                <Input type="number" min={1} value={v.required_checks} onChange={set(k, "required_checks")} className="h-8 text-xs font-mono"
                       data-testid={`settings-required-${k.toLowerCase()}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#003366] hover:bg-[#003366]/90" data-testid="settings-save">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
